import React, { useEffect } from 'react';
import { Maximize, AppWindow, Trash2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useMap } from '@/providers/MapProvider';
import VectorLayer from 'ol/layer/Vector';
import {
  AreaUnit,
  DistanceUnit,
  useMeasureTool,
} from '../../hooks/useMeasureTool';

export const MeasurePanel = () => {
  const { map } = useMap();
  const [distanceUnit, setDistanceUnit] = React.useState<DistanceUnit>('feet');
  const [areaUnit, setAreaUnit] = React.useState<AreaUnit>('sqft');

  const {
    startMeasuring,
    stopMeasuring,
    clearMeasurements,
    activeMeasureType,
  } = useMeasureTool(distanceUnit, areaUnit);

  useEffect(() => {
    if (!activeMeasureType) {
      startMeasuring('LineString');
    }

    return () => {
      stopMeasuring();
    };
  }, []);

  const handleClear = () => {
    clearMeasurements();

    if (map) {
      const measureLayer = map
        .getLayers()
        .getArray()
        .find((l) => l.get('id') === 'measure-layer');
      if (measureLayer instanceof VectorLayer) {
        measureLayer.getSource()?.clear();
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground border border-border">
        <p className="flex items-center gap-1.5 font-medium text-foreground mb-1">
          <ChevronRight className="w-3.5 h-3.5 text-primary" /> Instructions
        </p>
        <ul className="space-y-1 pl-5 list-disc">
          <li>Click to add points</li>
          <li>Double-click to finish</li>
        </ul>
      </div>

      {/* Mode Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => startMeasuring('LineString')}
          className={cn(
            'h-8 justify-start px-3 text-xs font-medium transition-all',
            activeMeasureType === 'LineString'
              ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
              : 'hover:bg-muted text-muted-foreground',
          )}
        >
          <Maximize size={14} className="mr-2" /> Distance
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => startMeasuring('Polygon')}
          className={cn(
            'h-8 justify-start px-3 text-xs font-medium transition-all',
            activeMeasureType === 'Polygon'
              ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
              : 'hover:bg-muted text-muted-foreground',
          )}
        >
          <AppWindow size={14} className="mr-2" /> Area
        </Button>
      </div>

      {/* Units Selector */}
      <div className="space-y-2">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Units
        </Label>
        <select
          value={activeMeasureType === 'LineString' ? distanceUnit : areaUnit}
          onChange={(e) =>
            activeMeasureType === 'LineString'
              ? setDistanceUnit(e.target.value as DistanceUnit)
              : setAreaUnit(e.target.value as AreaUnit)
          }
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer text-foreground"
        >
          {activeMeasureType === 'LineString' ? (
            <>
              <option value="feet">Feet (ft)</option>
              <option value="meters">Meters (m)</option>
              <option value="miles">Miles (mi)</option>
              <option value="kilometers">Kilometers (km)</option>
            </>
          ) : (
            <>
              <option value="sqft">Square Feet (ft²)</option>
              <option value="sqm">Square Meters (m²)</option>
              <option value="acres">Acres</option>
              <option value="sqmi">Square Miles (mi²)</option>
              <option value="sqkm">Square Kilometers (km²)</option>
            </>
          )}
        </select>
      </div>

      {/* Clear Button */}
      <div className="pt-3 border-t border-border mt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 h-8 text-xs font-medium"
        >
          <Trash2 size={13} className="mr-2" /> Clear All
        </Button>
      </div>
    </div>
  );
};
